<template>
  <DetailInfo class="screen-info-container" :btnList="btnList">
    <div v-rect-loading="ratingLoading" class="rating-container">
      <!-- 招聘经理评估 -->
      <div v-if="hasHmOverallRating" class="hm-overall-rating" :class="hmOverallRatingCollapsed && 'is-collapsed'">
        <div class="overall-rating-header">
          <div class="overall-rating-left">
            <div class="overall-rating-title-row" @click="toggleHmOverallRating">
              <span class="overall-rating-title">{{ $t(`${i18nPrefix}.title.hiringManagerRating`) }}</span>
              <i class="el-icon-arrow-down overall-rating-arrow" :class="hmOverallRatingCollapsed && 'is-collapsed'"></i>
            </div>
            <div v-if="activeHmOverallRating && !hmOverallRatingCollapsed" class="overall-rating-control">
              <div class="overall-rating-tabs">
                <div
                  v-for="(item, index) in hmOverallRatingList"
                  :key="item.id"
                  class="overall-rating-tab"
                  :class="index === activeHmOverallRatingIndex && 'is-active'"
                  @click="handleHmOverallRatingTabClick(index)"
                >
                  <span>{{ item.label }}</span>
                  <span class="overall-rating-tab-score">
                    <RecommendationResultChart
                      class="overall-rating-tab-score-value"
                      ringBackgroundColor="#E4E7ED"
                      :decision="item.decision"
                      :score="item.score"
                      :completion="item.completion"
                      :hmInfoList="item.hmInfoList"
                      :fitScoreStyle="overallRatingTabScoreStyle"
                      :chartOption="overallRatingTabScoreChartOption"
                      :circleWidth="2"
                      :fontSize="14"
                    />
                  </span>
                </div>
              </div>
              <div class="overall-rating-time">{{ activeHmOverallRatingTime }}</div>
            </div>
          </div>
          <div class="overall-rating-score">
            <RecommendationResultChart
              class="overall-score-circle"
              ringBackgroundColor="#E4E7ED"
              :decision="activeHmOverallRating.decision"
              :score="activeHmOverallRating.score"
              :completion="activeHmOverallRating.completion"
              :hmInfoList="activeHmOverallRating.hmInfoList"
              :fitScoreStyle="overallScoreStyle"
              :chartOption="overallScoreChartOption"
              :circleWidth="4"
              :fontSize="30"
            />
            <div class="overall-score-label">{{ $t(`${i18nPrefix}.label.overall`) }}</div>
          </div>
        </div>
        <div v-show="!hmOverallRatingCollapsed" class="overall-rating-detail">
          <HmInterviewEvaluation
            v-if="activeHmOverallRating.type === HM_OVERALL_RATING_TYPE.NEW"
            class="hm-interview-evaluation-section"
            result-type="rating"
            :evaluation="activeHmOverallRating.evaluation"
          />
          <RatingWrapper
            v-else
            :ratingList="activeHmOverallRating.ratingList"
          />
        </div>
      </div>

      <!-- 申请人前置问卷 -->
      <CollapseContent v-if="openQuestionList.prescreenQuestionList.length > 0" class="prescreen-questionnaire" :title="$t(`${i18nPrefix}.title.prescreenQuestionnaire`)">
        <QuestionWrapper
          :questionnaireResultList="openQuestionList.prescreenQuestionList"
          @videoPlayIconClick="openVideoDialog"
          @viewAttachmentBtnClick="openAttachment"
        >
        </QuestionWrapper>
      </CollapseContent>

      <!-- 申请人调查问卷 -->
      <CollapseContent v-if="openQuestionList.standardQuestionList.length > 0" class="job-questionnaire" :title="$t(`${i18nPrefix}.title.jobQuestionnaireResponse`)">
        <QuestionWrapper
          :questionnaireResultList="openQuestionList.standardQuestionList"
          @videoPlayIconClick="openVideoDialog"
          @viewAttachmentBtnClick="openAttachment"
        >
        </QuestionWrapper>
      </CollapseContent>

      <!-- 招聘人员筛选 -->
      <CollapseContent v-if="openQuestionList.hrQuestionList.length > 0" class="hr-questionnaire" :title="$t(`${i18nPrefix}.title.hrQuestionnaire`)">
        <QuestionWrapper
          isHr
          :questionnaireResultList="openQuestionList.hrQuestionList"
          @videoPlayIconClick="openVideoDialog"
          @viewAttachmentBtnClick="openAttachment"
        >
        </QuestionWrapper>
      </CollapseContent>

      <!-- 特定的问题 -->
      <CollapseContent v-if="openQuestionList.inviteQuestionList.length > 0" class="invited-question" :title="$t(`${i18nPrefix}.title.specificQuestion`)">
        <template #suffix>
          <el-button
            v-if="!candidateBaseInfo.had_finished_invitation && !candidateBaseInfo.is_agency_uploaded_and_hid"
            v-loading="noFinishedBtn.loading"
            class="invite-btn"
            type="text"
            element-loading-spinner="el-icon-loading"
            :class="noFinishedBtn.invited && 'invited'"
            :disabled="isDisabled"
            @click.stop="handleInviteNoFinishedBtnClick"
          >{{ noFinishedBtn.invited ? $t(`${i18nPrefix}.label.inviteSuccess`) : $t(`${i18nPrefix}.btn.inviteNoFinishedOpenQuestion`) }}</el-button>
        </template>
        <QuestionWrapper
          :questionnaireResultList="openQuestionList.inviteQuestionList"
          @videoPlayIconClick="openVideoDialog"
          @viewAttachmentBtnClick="openAttachment"
        >
        </QuestionWrapper>
      </CollapseContent>
    </div>
    <DialogWrapper
      hideFooter
      class="video-player-dialog"
      :title="popInfo.title"
      :show.sync="popInfo.showVideoPop"
      @closed="closeVideoDialog"
    >
      <div class="video-container">
        <video v-if="popInfo.showVideoPop && popInfo.videoUrl" controls class="video" autoplay="autoplay" width="100%" :src="popInfo.videoUrl"></video>
      </div>
    </DialogWrapper>
    <InviteOpenQuestion
      ref="inviteQuestionDialog"
      :jobId="jobId"
      :candidateId="candidateId"
      :applicationList="applicationList"
      :hiddenVideoQuestion="hiddenVideoQuestion"
      :hiddenAttachmentQuestion="hiddenAttachmentQuestion"
      :hiddenQuestionTemplate="hiddenQuestionTemplate"
      @addInviteQuestion="addInviteQuestion"
      @success="handelInviteOpenQuestionSuccess"
    />
    <InviteHmRating
      ref="inviteRatingDialog"
      :applicationList="applicationList"
      @sent="handleSentHmRatingInvite"
    />
    <QuestionnaireSelectorDialog
      ref="questionnaireSelectorDialog"
      :show.sync="showQuestionnaireSelector"
      :selectedQuestionnaire="selectedQuestionnaire"
      @confirm="handleConfirmQuestionnaire"
    />
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import DetailInfo from './components/detailInfo.vue'
import RatingWrapper from './components/ratingsWrapper.vue'
import QuestionWrapper from './components/questionWrapper.vue'
import CollapseContent from './components/collapseContent.vue'
import HmInterviewEvaluation from './components/hmInterviewEvaluation.vue'
import InviteOpenQuestion from './dialog/inviteOpenQuestion.vue'
import InviteHmRating from '../../applicantList/dialog/inviteHmRating.vue'
import QuestionnaireSelectorDialog from '@/views/adminNew/applicantList/dialog/components/questionnaireSelectorDialog.vue'
import questionnaireResultsMixin from './mixins/questionnaireResultsMixin'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import { mapActions } from 'vuex'
import { addI18nProperty, getI18nPropertyValue, isEn } from '@/i18n'
import moment from 'moment'
import { HM_OVERALL_RATING_TYPE } from '@/constant/questionnaire'
import { INTERVIEW_RATING_DECISION, RATE_STATUS_MAP } from '@/constant/interview'
import RecommendationResultChart from '@/views/adminNew/components/recommendationResultChart.vue'

export default {
  mixins: [questionnaireResultsMixin],
  components: {
    DetailInfo,
    RatingWrapper,
    QuestionWrapper,
    CollapseContent,
    HmInterviewEvaluation,
    InviteOpenQuestion,
    InviteHmRating,
    QuestionnaireSelectorDialog,
    DialogWrapper,
    RecommendationResultChart
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    hiddenVideoQuestion: {
      default: true
    },
    hiddenAttachmentQuestion: {
      default: true
    },
    hiddenQuestionTemplate: {
      default: false
    },
    hasHrQuestion: {
      type: Boolean,
      default: false
    },
    isTalentPool: {
      type: Boolean,
      default: false
    },
    isAgency: {
      type: Boolean,
      default: false
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      HM_OVERALL_RATING_TYPE,
      popInfo: {
        showVideoPop: false,
        title: null,
        videoUrl: null,
        oggUrl: null
      },
      noFinishedBtn: {
        loading: false,
        invited: false
      },
      openQuestionList: {
        prescreenQuestionList: [],
        standardQuestionList: [],
        hrQuestionList: [],
        inviteQuestionList: []
      },
      showQuestionnaireSelector: false,
      selectedQuestionnaire: null,
      hmRatingQuestionnaireResults: null,
      activeHmOverallRatingIndex: 0,
      hmOverallRatingCollapsed: false,
      overallScoreStyle: {
        width: '70px',
        height: '70px'
      },
      overallScoreChartOption: {
        series: {
          data: [{
            title: {
              fontWeight: 500,
              offsetCenter: [0, '10%']
            }
          }]
        }
      },
      overallRatingTabScoreStyle: {
        width: '32px',
        height: '32px'
      },
      overallRatingTabScoreChartOption: {
        series: {
          data: [{
            title: {
              fontWeight: 500
            }
          }]
        }
      },
      ratingLoading: false
    }
  },
  computed: {
    legacyHmRateList () {
      return _.filter(this.candidateBaseInfo.rate_list, item => item && item.source_type === 'legacy')
    },
    hasLegacyHmOverallRating () {
      return this.legacyHmRateList.length > 0
    },
    hasNewHmOverallRating () {
      return Boolean(this.hmRatingQuestionnaireResults && this.hmRatingQuestionnaireResults.result_list.length)
    },
    hasHmOverallRating () {
      return this.hmOverallRatingList.length > 0
    },
    hmOverallRatingList () {
      const list = []
      if (this.hasNewHmOverallRating) {
        const questionnaireOverallScoreMap = _.get(this.hmRatingQuestionnaireResults, 'overall_total_score.questionnaire')
        const questionnaireOverallCompletionMap = _.get(this.hmRatingQuestionnaireResults, 'overall_completion_rate')
        const questionnaireOverallDecisionMap = _.get(this.hmRatingQuestionnaireResults, 'overall_decision')
        const questionnaireHmInfoMap = _.get(this.hmRatingQuestionnaireResults, 'hire_manager_map')
        _.forEach(this.groupQuestionnaireResults(this.hmRatingQuestionnaireResults, questionnaireOverallScoreMap), ratingResult => {
          const evaluation = this.normalizeQuestionnaireResultsEvaluation(ratingResult)
          const ratingInvitation = _.find(ratingResult.result_list, record => record.invite_time)
          list.push({
            id: `new-hm-overall-rating-${ratingResult.uuid}`,
            type: HM_OVERALL_RATING_TYPE.NEW,
            score: this.formatNullableScore(ratingResult.overall_total_score),
            scoreTotal: evaluation.hr_score_total,
            decision: _.get(questionnaireOverallDecisionMap, [ratingResult.uuid], INTERVIEW_RATING_DECISION.NOT_RATE_YET),
            completion: _.get(questionnaireOverallCompletionMap, [ratingResult.uuid], null),
            hmInfoList: _.get(questionnaireHmInfoMap, [ratingResult.uuid], []),
            inviteTime: ratingInvitation && ratingInvitation.invite_time,
            evaluation
          })
        })
      }
      if (this.hasLegacyHmOverallRating) {
        list.push({
          id: 'legacy-hm-overall-rating',
          type: HM_OVERALL_RATING_TYPE.LEGACY,
          score: this.formatNullableScore(_.meanBy(this.legacyHmRateList, item => Number(item.total_rate))),
          decision: this.getHmOverallRatingRecommendation(_.map(this.legacyHmRateList, item => RATE_STATUS_MAP[item.status])),
          completion: 100,
          hmInfoList: _.map(this.legacyHmRateList, item => {
            return {
              id: item.id,
              full_name: item.hm_name,
              score: this.formatNullableScore(item.total_rate),
              rating_decision: this.getHmOverallRatingRecommendation([RATE_STATUS_MAP[item.status]])
            }
          }),
          inviteTime: this.getLatestLegacyHmRatingInviteTime(),
          ratingList: this.legacyHmRateList
        })
      }
      return _.chain(list)
        .sortBy([
          item => item.type === HM_OVERALL_RATING_TYPE.LEGACY ? 0 : 1,
          item => item.inviteTime ? moment(item.inviteTime).valueOf() : Infinity
        ])
        .map((item, index) => ({
          ...item,
          label: this.getHmOverallRatingLabel(index, list.length)
        }))
        .value()
    },
    activeHmOverallRating () {
      return this.hmOverallRatingList[this.activeHmOverallRatingIndex] || this.hmOverallRatingList[0]
    },
    activeHmOverallRatingTime () {
      const rating = this.activeHmOverallRating
      if (!rating) {
        return '-'
      }
      if (!rating.inviteTime) {
        return '-'
      }
      const isEnglish = isEn()
      return moment(rating.inviteTime).locale(isEnglish ? 'en' : 'zh-cn').format(isEnglish ? 'dddd, MMM Do, YYYY HH:mm' : 'dddd, YYYY年MM月DD日, HH:mm')
    },
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job.id
    },
    hasInviteORQuestionPermission () {
      return this.candidateBaseInfo.company.is_elc_origin_company
    },
    btnList () {
      const vm = this
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid) {
        return []
      }
      const list = [
        {
          label: this.$t(`${this.i18nPrefix}.btn.inviteAnswerQuestion`),
          onclick: this.onInviteOpenQuestionBtnClick
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.editOpenQuestion`),
          onclick: this.onEditOpenQuestion,
          hidden: this.isTalentPool || this.isAgency || !this.canOpenQuestionScreenType || !this.hasHrQuestion
        }
      ]
      if (this.hasAnyPermission(['PERM_INVITE_HM_REVIEW', 'PREMIUM_INVITE_HM_REVIEW'])) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.btn.hiringManagerRating`),
          onclick: this.onInviteHmRatingBtnClick
        })
      }
      if (this.hasInviteORQuestionPermission) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.btn.inviteAnswerORQuestion`),
          onclick: this.onInviteQRQuestionBtnClick
        })
      }
      if (!this.isAgency && this.hasAnyPermission(['PERM_INVITE_HM_REVIEW', 'PREMIUM_INVITE_HM_REVIEW'])) {
        list.push({
          label: this.$t(`${this.i18nPrefix}.btn.startRating`),
          onclick: this.onStartRatingBtnClick
        })
      }
      _.forEach(list, item => {
        Object.defineProperty(item, 'disabled', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled
          }
        })
        Object.defineProperty(item, 'tooltip', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled && vm.$t(`${vm.i18nPrefix}.message.disabledTip`)
          }
        })
      })
      return list
    },
    applicationList () {
      return [{
        job: this.candidateBaseInfo.job,
        candidate: {
          id: this.candidateBaseInfo.id,
          name: this.candidateBaseInfo.other_name
        }
      }]
    },
    canOpenQuestionScreenType () {
      if (this.isTalentPool) {
        return
      }
      // 前置问卷 , 调查问卷, 招聘人员筛选 按照这个顺序有则直接返回问卷类型
      const hasPrescreenQuestion = this.candidateBaseInfo.job.has_prescreen_question
      const hasStandardQuestion = this.candidateBaseInfo.job.has_standard_question
      const hasHrQuestion = this.candidateBaseInfo.job.has_hr_question
      if (hasPrescreenQuestion) {
        return 'PRESCREEN_QUESTIONNAIRE'
      } else if (hasStandardQuestion) {
        return 'JOB_QUESTIONNAIRE'
      } else if (hasHrQuestion) {
        return 'HR_QUESTIONNAIRE'
      } else {
        return null
      }
    }
  },
  created () {
    if (!this.isAgency) {
      this.loadHrRatingQuestionnaireResults()
    }
    this.loadPrescreenQuestionList()
    this.loadStandardQuestionList()
    this.loadHrQuestionList()
    this.loadInviteQuestionList()
    this.$eventBus.$on('refreshHrQuestion', this.loadHrQuestionList)
  },
  watch: {
    hmOverallRatingList (list) {
      if (this.activeHmOverallRatingIndex > list.length - 1) {
        this.activeHmOverallRatingIndex = 0
      }
    }
  },
  methods: {
    ...mapActions('common', ['getPrivateFileUrl']),
    ...mapActions('application', ['sendInviteNotification', 'sendInviteAnswerOriginCompanyQuestion', 'getApplicationOpenQuestionInfo', 'createHrRating', 'getHrRatingQuestionnaireResults']),
    getHmOverallRatingLabel (index, total) {
      if (total === 1) {
        return this.$t(`${this.i18nPrefix}.label.rating`)
      }
      return this.$t(`${this.i18nPrefix}.label.ratingSequence`, { number: index + 1 })
    },
    getLatestLegacyHmRatingInviteTime () {
      const invitedRatings = _.filter(this.legacyHmRateList, item => item.invite_time)
      const latestRating = _.maxBy(invitedRatings, item => moment(item.invite_time).valueOf())
      return latestRating && latestRating.invite_time
    },
    getHmOverallRatingRecommendation (recommendations) {
      const recommendationList = _.uniq(_.compact(recommendations))
      if (!recommendationList.length) {
        return INTERVIEW_RATING_DECISION.NOT_RATE_YET
      }
      if (recommendationList.length === 1) {
        return recommendationList[0]
      }
      const hasRecommended = recommendationList.includes(INTERVIEW_RATING_DECISION.RECOMMENDED)
      const hasNotRecommended = recommendationList.includes(INTERVIEW_RATING_DECISION.NOT_RECOMMENDED)
      if (hasRecommended && hasNotRecommended) {
        return INTERVIEW_RATING_DECISION.CONFLICT
      }
      if (hasRecommended) {
        return INTERVIEW_RATING_DECISION.RECOMMENDED
      }
      if (hasNotRecommended) {
        return INTERVIEW_RATING_DECISION.NOT_RECOMMENDED
      }
      return INTERVIEW_RATING_DECISION.PENDING
    },
    toggleHmOverallRating () {
      this.hmOverallRatingCollapsed = !this.hmOverallRatingCollapsed
    },
    handleHmOverallRatingTabClick (index) {
      this.activeHmOverallRatingIndex = index
    },
    async loadHrRatingQuestionnaireResults () {
      if (!this.jobId || !this.candidateId) {
        return
      }
      try {
        const resp = await this.getHrRatingQuestionnaireResults({
          job_id: this.jobId,
          candidate_id: this.candidateId
        })
        this.hmRatingQuestionnaireResults = resp || null
      } catch (e) {
        this.hmRatingQuestionnaireResults = null
      } finally {
        this.ratingLoading = false
      }
    },
    async loadQuestionList (questionType) {
      const params = {
        job_id: this.jobId,
        candidate_id: this.candidateId,
        question_type: questionType,
        has_answered: 1
      }
      const resp = await this.getApplicationOpenQuestionInfo(params)
      if (resp) {
        return resp
      }
    },
    async loadPrescreenQuestionList () {
      const resp = await this.loadQuestionList('prescreen')
      if (resp) {
        this.openQuestionList.prescreenQuestionList = resp
      }
    },
    async loadStandardQuestionList () {
      const resp = await this.loadQuestionList('standard')
      if (resp) {
        this.openQuestionList.standardQuestionList = resp
      }
    },
    async loadHrQuestionList () {
      const resp = await this.loadQuestionList('hr')
      if (resp) {
        this.openQuestionList.hrQuestionList = resp
      }
    },
    async loadInviteQuestionList () {
      const resp = await this.loadQuestionList('invite')
      if (resp) {
        this.openQuestionList.inviteQuestionList = resp
      }
    },
    closeVideoDialog () {
      this.popInfo.showVideoPop = false
      this.popInfo.title = null
      this.popInfo.videoUrl = null
      this.popInfo.oggUrl = null
    },
    async openVideoDialog (question, callback) {
      const resp = await this.getPrivateFileUrl({ fileType: 'OPEN_QUESTION_VIDEO', fileKey: question.answer_file_key })
      const popInfo = this.popInfo
      popInfo.title = question.title
      popInfo.videoUrl = resp.video
      popInfo.oggUrl = resp.ogg
      callback()
      popInfo.showVideoPop = true
    },
    async openAttachment (question, callback) {
      const fileUrl = await this.getPrivateFileUrl({ fileType: 'OPEN_QUESTION_ATTACHMENT', fileKey: question.answer_file_key })
      const candidateName = this.candidateBaseInfo.other_name
      const jobName = getI18nPropertyValue(this.candidateBaseInfo.job, 'cn_title', 'title')
      const fileExt = question.answer_file_key.split('.')[1]
      let fileName = `${candidateName}-${jobName}-${question.title}`
      if (fileExt) {
        fileName += '.' + fileExt
      }
      window.open(`${fileUrl}&attname=${encodeURIComponent(fileName)}`)
      callback()
    },
    onInviteOpenQuestionBtnClick () {
      this.$refs.inviteQuestionDialog.openDialog()
    },
    onEditOpenQuestion () {
      const jobId = this.jobId
      const candidateApplyJobId = this.candidateBaseInfo.candidate_apply_job_id
      const back = window.encodeURIComponent(this.$route.fullPath)
      if (this.isAgency) {
        window.open(`/v-agency/application/job/${jobId}/edit/${candidateApplyJobId}?back=${back}&step=${this.canOpenQuestionScreenType}`)
      } else {
        this.$eventBus.$emit('showCollapseInfo', 'screening')
      }
    },
    async onInviteQRQuestionBtnClick () {
      const params = {
        candidate_ids: [this.candidateId],
        job_id: this.jobId
      }
      const resp = await this.sendInviteAnswerOriginCompanyQuestion(params)
      if (resp) {
        const successCount = resp.send_success_count
        this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteORQuestion`, { inviteCount: 1, successCount: successCount }))
      }
    },
    onInviteHmRatingBtnClick () {
      this.$refs.inviteRatingDialog.showDialog()
    },
    handleSentHmRatingInvite (inviteResult) {
      this.candidateBaseInfo.current_status = addI18nProperty(inviteResult.candidate_status, 'i18nName', 'name_cn', 'name_en')
      if (this.isAgency) {
        return
      }
      this.ratingLoading = true
      this.$nextTick(() => {
        setTimeout(async () => {
          this.$emit('refreshProfileTab')
          await this.loadHrRatingQuestionnaireResults()
          this.activeHmOverallRatingIndex = Math.max(this.hmOverallRatingList.length - 1, 0)
        })
      })
    },
    async handleInviteNoFinishedBtnClick () {
      if (this.noFinishedBtn.loading || this.noFinishedBtn.invited) {
        return
      }
      this.noFinishedBtn.loading = true
      try {
        const resp = await this.sendInviteNotification({
          type_id: 3,
          candidate_ids: [this.candidateId],
          job_id: this.jobId
        })
        this.noFinishedBtn.invited = true
        if (resp.success && resp.success.length > 0) {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.success`))
        } else if (resp.no_email_count && resp.no_email_count.length > 0) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.noEmailAccount`))
        } else if (resp.ignore && resp.ignore.length > 0) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.hasFinished`))
        } else {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.inviteFinishProfile.fail`))
          this.noFinishedBtn.invited = false
        }
      } finally {
        this.noFinishedBtn.loading = false
      }
    },
    addInviteQuestion (questionList) {
      // 由于页面上显示的问题顺序不是按照邀请时间来的，所以这里将问题放到最后去
      // this.openQuestionList.inviteQuestionList.splice(0, 0, ...questionList)
      // 需要检查问题是不是已经邀请过了，已经邀请过的问题会跳过
      _.forEach(questionList, (item) => {
        if (!this.checkQuestionDuplicate(this.openQuestionList.inviteQuestionList, item)) {
          this.openQuestionList.inviteQuestionList.push(item)
        }
      })
    },
    checkQuestionDuplicate (questionList, questionData) {
      return _.find(questionList, (item) => {
        return item.type === questionData.type && item.title === questionData.title
      })
    },
    handelInviteOpenQuestionSuccess (resp) {
      if (resp && resp.candidate_status) {
        this.candidateBaseInfo.current_status = addI18nProperty(resp.candidate_status, 'i18nName', 'name_cn', 'name_en')
      }
    },
    async onStartRatingBtnClick () {
      this.selectedQuestionnaire = null
      await this.loadDefaultRatingQuestionnaire()
      this.showQuestionnaireSelector = true
    },
    async loadDefaultRatingQuestionnaire () {
      const defaultQuestionnaireId = _.get(this.candidateBaseInfo, 'job.default_rating_questionnaire_id')
      if (!defaultQuestionnaireId) {
        return
      }
      try {
        this.selectedQuestionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(defaultQuestionnaireId)
      } catch (e) {
        this.selectedQuestionnaire = null
      }
    },
    async handleConfirmQuestionnaire (questionnaire) {
      this.selectedQuestionnaire = questionnaire
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.createHrRating({
          job_id: this.jobId,
          candidate_id: this.candidateId,
          questionnaire_id: questionnaire?.id
        })
        if (resp) {
          window.open(`/recruiter-pc/v-admin/hm/candidate/profile/screening?candidate_id=${this.candidateId}&job_id=${this.jobId}&uuid=${resp.uuid}`)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.screen-info-container {
  .rating-container {
    margin: 0 auto;

    .hm-overall-rating {
      padding-bottom: 20px;

      .overall-rating-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        width: 100%;

        .overall-rating-left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
          min-width: 0;

          .overall-rating-title-row {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 0;
            border: 0;
            color: #333;
            background: transparent;
            cursor: pointer;

            .overall-rating-title {
              font-size: 18px;
              line-height: 48px;
              font-weight: bold;
              white-space: nowrap;
            }

            .overall-rating-arrow {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              font-size: 18px;
              transition: transform .3s;
              transform: rotate(-180deg);

              &.is-collapsed {
                transform: rotate(0deg);
              }
            }
          }

          .overall-rating-control {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
            min-width: 0;

            .overall-rating-tabs {
              position: relative;
              display: flex;
              align-items: center;
              min-height: 34px;

              .overall-rating-tab {
                display: flex;
                align-items: center;
                height: 20px;
                padding: 0 14px;
                border: 0;
                border-right: 1px solid #DCDFE6;
                color: #606266;
                background: transparent;
                cursor: pointer;
                font-size: 14px;
                line-height: 20px;
                white-space: nowrap;

                &:first-child {
                  padding-left: 0;
                }

                &:last-child {
                  border-right: 0;
                }

                &.is-active {
                  color: #0096CC;
                  font-weight: 600;
                }

                .overall-rating-tab-score {
                  margin-left: 6px;
                  width: 32px;
                  height: 32px;
                  border-radius: 50%;
                  background-color: #fff;
                  box-sizing: border-box;

                  .overall-rating-tab-score-value {
                    display: inline-block;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                  }
                }
              }
            }

            .overall-rating-time {
              color: #606266;
              font-size: 14px;
              line-height: 14px;
              white-space: nowrap;
            }
          }
        }

        .overall-rating-score {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;

          .overall-score-circle {
            width: 70px;
            height: 70px;
            border-radius: 50%;
            background-color: #fff;
          }

          .overall-score-label {
            color: #909399;
            font-size: 14px;
            line-height: 14px;
          }
        }
      }

      &.is-collapsed {
        padding-bottom: 0;

        .overall-rating-header {
          justify-content: flex-start;
          gap: 0;
        }

        .overall-rating-left {
          gap: 0;
        }
      }

      .overall-rating-detail {
        margin-top: 20px;

        ::v-deep .ratings-container {
          margin-top: 0;
          margin-right: 0;
        }
      }
    }

    .invited-question {
      .invite-btn {
        margin-left: 15px;

        &.invited {
          color: #606266;
        }

        ::v-deep .el-loading-mask {
          .el-loading-spinner {
            font-size: 22px;
            margin-top: -11px;
          }
        }
      }
    }

    .video-player-dialog {
      ::v-deep .el-dialog__body {
        height: 60vh;
      }

      .video-container {
        height: calc(100% - 50px);
        text-align: center;

        .video {
          width: 100%;
          height: 100%;
        }
      }
    }
  }

  .collapse-content-wrapper {
    ::v-deep {
      .collapse-content {
        .content {
          padding-bottom: 20px;
        }
      }
    }
  }
}
</style>
